import { useEffect, useRef, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";

import {
  keyboardAwareOverlayPadding,
  keyboardAwareSheetMaxHeight,
} from "../../lib/native/keyboardInset";
import { useKeyboardInsetWhen } from "../../lib/native/KeyboardInsetProvider";
import { useBottomSheetStack } from "./bottomSheetStack";

export { BottomSheetStackProvider } from "./bottomSheetStack";

type BottomSheetProps = {
  open: boolean;
  onClose: () => void;
  children: ReactNode;
  labelledBy?: string;
  maxHeight?: string;
};

/** Slide-up bottom sheet with a fading backdrop. Honors reduced motion. Portaled to `document.body`. */
export function BottomSheet({
  open,
  onClose,
  children,
  labelledBy,
  maxHeight = "85svh",
}: BottomSheetProps) {
  const prefersReducedMotion = useReducedMotion();
  const sheetRef = useRef<HTMLElement>(null);
  const keyboardState = useKeyboardInsetWhen(open);
  const { zIndex, isTop } = useBottomSheetStack(open, onClose);
  const overlayPaddingBottom = keyboardAwareOverlayPadding(keyboardState.bottomInset);
  const sheetMaxHeight = keyboardAwareSheetMaxHeight(maxHeight, keyboardState);

  useEffect(() => {
    if (!open) return;
    const sheet = sheetRef.current;
    if (!sheet) return;

    const onFocusIn = (event: FocusEvent) => {
      const target = event.target;
      if (!(target instanceof HTMLElement)) return;
      if (!target.matches("input, textarea, select, [contenteditable='true']")) return;
      requestAnimationFrame(() => {
        target.scrollIntoView({ block: "nearest", behavior: prefersReducedMotion ? "auto" : "smooth" });
      });
    };

    sheet.addEventListener("focusin", onFocusIn);
    return () => sheet.removeEventListener("focusin", onFocusIn);
  }, [open, prefersReducedMotion]);

  const sheetTransition = prefersReducedMotion
    ? { duration: 0 }
    : { type: "spring" as const, stiffness: 420, damping: 40, mass: 0.9 };

  if (typeof document === "undefined") return null;

  return createPortal(
    <AnimatePresence>
      {open ? (
        <motion.div
          key="bottom-sheet"
          className="fixed inset-0 flex flex-col justify-end"
          style={{
            zIndex,
            ...(overlayPaddingBottom !== undefined ? { paddingBottom: overlayPaddingBottom } : {}),
          }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: prefersReducedMotion ? 0 : 0.2 }}
        >
          <motion.div
            className="absolute inset-0 bg-black/50"
            style={{ opacity: isTop ? 1 : 0 }}
            aria-hidden="true"
            onPointerDown={isTop ? onClose : undefined}
          />
          <motion.section
            ref={sheetRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby={labelledBy}
            className={`liquid-glass-sheet relative z-10 flex min-h-0 flex-col overflow-hidden pb-[max(18px,env(safe-area-inset-bottom))] ${
              isTop ? "" : "pointer-events-none origin-bottom scale-[0.97] brightness-[0.88]"
            }`}
            style={{ maxHeight: sheetMaxHeight }}
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "100%" }}
            transition={sheetTransition}
            onPointerDown={(event) => event.stopPropagation()}
          >
            {children}
          </motion.section>
        </motion.div>
      ) : null}
    </AnimatePresence>,
    document.body,
  );
}
